import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@app/context/ModalsContext'

import { Input } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { Box } from '@shared/components/Box'
import { CopyButton } from '@shared/components/CopyButton'

interface Props {
  shortenedReferralLink: string
  referralLink: string
  landingPageUrl: string
  isShortLinkLoading: boolean
  ibCode: string
  domain: string
  urlId: string
  isCustomUrl: boolean
  generateShortenUrl: boolean
}

export const QrCodeModal: BaseModalType<Props> = ({
  shortenedReferralLink,
  referralLink,
  landingPageUrl,
  isShortLinkLoading,
  isCustomUrl,
  domain,
  ibCode,
  generateShortenUrl,
  close,
}) => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)

  const signUpDefaultUrl = new URL(referralLink).searchParams.get('lp') ?? ''

  const qrCodeParams = {
    domain,
    isCustomUrl,
    urlId: shortenedReferralLink || landingPageUrl || signUpDefaultUrl,
    ibCode,
    generateShortenUrl,
  }

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.ib.qrCode(qrCodeParams as unknown as Record<string, unknown>),
    queryFn: () => apiClient.getQRCode(qrCodeParams),
  })

  useEffect(() => {
    if (data) {
      if (data instanceof Blob) {
        const url = URL.createObjectURL(data)
        setQrCodeUrl(url)

        return () => {
          URL.revokeObjectURL(url)
        }
      }

      if (typeof data === 'string') {
        setQrCodeUrl(data)
      }
    }

    return () => {
      if (qrCodeUrl) {
        URL.revokeObjectURL(qrCodeUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const referralLinkValue = shortenedReferralLink || referralLink || ''

  return (
    <Modal size='sm'>
      <Modal.Body centered isLoading={isLoading}>
        {qrCodeUrl ? (
          <Modal.Body.Image image={qrCodeUrl} alt='qr-code' />
        ) : (
          <Modal.Body.Text>{t('errors.Something went wrong')}</Modal.Body.Text>
        )}

        <Box flex gap='x12' fullWidth justify='space-between'>
          <Input
            value={referralLinkValue}
            label={'Referral link'}
            disabled={isShortLinkLoading}
            size='lg'
            fullWidth
          />

          <CopyButton value={referralLinkValue} disabled={!referralLinkValue} />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Close')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary
          link={{ href: qrCodeUrl || '', download: 'qr-code.jpeg' }}
          disabled={!qrCodeUrl}
        >
          {t('Download')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
